/* eslint-disable react/jsx-no-target-blank */
import {getTarget} from "@COMPONENTS/common/commonUtil";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import useCountdown from "@/hooks/common/useCountdown";
import {TDetailPackage} from "@COMPONENTS/product/types";
import * as style from "@COMPONENTS/style/detailpackage/itineraryM";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import useGlobalGoodsLive, {GlobalGoodsLive} from "@QUERIES/bff/global/useGlobalGoodsLive";
import useBannerContents, {BannerContents as BannerContentsType} from "@QUERIES/essential/banner/useBannerContents";
import useBannerGoodsDeal, {BannerGoodsDeal} from "@QUERIES/essential/banner/useBannerGoodsDeal";
import {format} from "@yb/utils";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import {memo} from "react";
import {useTaggingClick} from "@/hooks/common/useTagging";
import SSOLink from "@COMPONENTS/common/ssoLink";
import bannerYellowpick from "@PUBLIC/M/images/content/event/yellowpick/banner_yellowpick_571.png";
import useGoodsBannerPick, {GoodsBannerPick} from "@QUERIES/portal/useGoodsBannerPick";

const daykor = ["일", "월", "화", "수", "목", "금", "토"];

const BannerContents = ({contents}: {contents: BannerContentsType[]}) => {
	const {taggingClick} = useTaggingClick("pkg_select_promo_dtl", {selectDiv: "MiddtlBanner"});
	return (
		<>
			{contents.length > 0 && (
				<>
					{contents.map((item, index) => (
						<style.EventBanner key={index}>
							<SSOLink
								target={getTarget(item.mTarget)}
								href={`${publicRuntimeConfig.ENV_PACKAGE_M_URL}${item.mLink}`}
								onClick={() => taggingClick(item)}
							>
								<img src={`${publicRuntimeConfig.ENV_CIMG_CDN_URL}${item.mImgPath}`} alt={item.mAlt} />
							</SSOLink>
						</style.EventBanner>
					))}
				</>
			)}
		</>
	);
};

const BannerYellowPangDeal = ({deal}: {deal: BannerGoodsDeal}) => {
	const PangdealText = () => {
		const targetDate = dayjs(deal.dealEdTm).add(1, "minute");
		const countdown = useCountdown(targetDate);
		return (
			<style.PriceBox>
				<p>
					<style.PangdealProduct>{deal.dealType === "T" ? "타임특가" : "선착순특가"}</style.PangdealProduct>
					&nbsp;
					<style.PangdealDate>
						{deal.dealType === "T" ? countdown ?? "종료" : `상품 ${deal.dealRervCnt || 0}개`}
					</style.PangdealDate>
					&nbsp;{countdown !== null && ` 남음`}
				</p>
				<p>{`${
					deal.dealRervCnt > 0 || countdown !== null
						? `${format.koKRFormatNumber(deal.price - deal.dealGoodsPrice)}원 즉시할인가격`
						: ``
				}`}</p>
			</style.PriceBox>
		);
	};

	return (
		<>
			{deal && (
				<SSOLink href={`${publicRuntimeConfig.ENV_PACKAGE_M_URL}/promotion/yellowPangDeal.yb`} target="_self">
					<style.PangdealPrice>
						{/* PangdealText */}
						<PangdealText />
					</style.PangdealPrice>
					<style.PangdealLink>
						<span>전체보기</span>
					</style.PangdealLink>
				</SSOLink>
			)}
		</>
	);
};

const BannerYellowLive = ({live}: {live: GlobalGoodsLive}) => {
	dayjs.extend(isBetween); // isBetween 사용
	const {dspYn, liveDt, liveTime} = live;
	// 방송 노출 조건 Y가 아닌 경우 - 노출 X
	if (dspYn !== "Y") return <></>;

	const liveStartDate = dayjs(liveDt);
	const liveEndDate = liveStartDate.add(Number(liveTime), "minute");

	const today = dayjs();

	const LiveText = () => {
		if (today.isBefore(liveStartDate)) {
			const countdown = useCountdown(liveStartDate);
			return (
				<div>
					<style.LiveDate>COMING SOON</style.LiveDate>
					<style.LiveState>{countdown}</style.LiveState>
				</div>
			);
		} else if (today.isBetween(liveStartDate, liveEndDate)) {
			return (
				<div>
					<style.LiveDate>ON AIR</style.LiveDate>
					<style.LiveState>실시간 방송 중</style.LiveState>
				</div>
			);
		} else {
			const formattedDate = `${liveStartDate.format("MM/DD")}(${daykor[liveStartDate.day()]}) ${liveStartDate.format(
				"HH:mm",
			)}`;
			return (
				<div>
					<style.LiveDate>{formattedDate}</style.LiveDate>
					<style.LiveState>방영 완료</style.LiveState>
				</div>
			);
		}
	};

	return (
		<style.BannerYellowLive>
			<SSOLink href={`${publicRuntimeConfig.ENV_PORTAL_M_URL}/promotion/yellowLive`}>
				<style.LiveText>
					<style.LiveTit>최대 혜택으로 떠나는 생생한 여행</style.LiveTit>
					{/* LiveText */}
					<LiveText />
				</style.LiveText>
				<style.LiveLink>
					<span>바로가기</span>
				</style.LiveLink>
			</SSOLink>
		</style.BannerYellowLive>
	);
};

const BannerYellowPick = ({pick}: {pick: GoodsBannerPick}) => {
	const link =
		pick.webBannerLink === "AUTOLINK"
			? `${publicRuntimeConfig.ENV_PACKAGE_M_URL}/promotion/promotionDetail.yb?mstNo=${pick.bnrMstNo}`
			: pick.mlink;
	return (
		<style.EventBanner>
			<SSOLink href={link}>
				<img src={bannerYellowpick} alt="옐로PICK 혜택 특가로 떠나는 이달의 여행지" />
			</SSOLink>
		</style.EventBanner>
	);
};

const DetailBannerComponent = (props: TDetailPackage) => {
	const {goodsCd} = props.goodsDetail;

	const bannerContentsFetch = useBannerContents("GOPM"); // WEB_CONT_DIVI_CD 상품상세_요금영역
	const bannerContents = bannerContentsFetch.data?.body || [];

	const bannerDealFetch = useBannerGoodsDeal(goodsCd);
	const bannerDeal = bannerDealFetch.data?.body;

	const bannerLiveFetch = useGlobalGoodsLive(goodsCd);
	const bannerLive = bannerLiveFetch.data?.body;

	const bannerPickFetch = useGoodsBannerPick(goodsCd);
	const bannerPick = bannerPickFetch.data?.body;

	return (
		<>
			{(bannerContents.length > 0 || bannerDeal || bannerLive) && (
				<style.EventBannerWrap>
					{bannerContents && <BannerContents contents={bannerContents} />}
					{bannerPick && <BannerYellowPick pick={bannerPick} />}
					{bannerDeal && <BannerYellowPangDeal deal={bannerDeal} />}
					{bannerLive && <BannerYellowLive live={bannerLive} />}
				</style.EventBannerWrap>
			)}
		</>
	);
};

const DetailBanner = (props: TDetailPackage) => (
	<ErrorBoundary logMsg="EventDetailBanner Error">
		<DetailBannerComponent {...props} />
	</ErrorBoundary>
);

export default memo(DetailBanner);
